import {
  enforce2Decimals,
  formatDollarAmount,
  formatedAmountToNumber,
} from '@/utils/amounts';
import { NOT_FOUND, parseNumberOrZero } from '@/utils/functions';
import { Dayjs } from 'dayjs';
import { WritableDraft } from 'immer';
import { Updater } from 'use-immer';
import { CartBuyerUi, ProductUi, SellerLineUi } from '../CartUiModel';
import { CreateEditCartUi } from './CreateEditUiModel';

const calcualteProductTotal = (product: ProductUi): number => {
  const isPiezaVariable =
    product.unitName === 'kg' && product.selectorUnitName === 'pz';
  const adjustedQuantity = isPiezaVariable
    ? product.averageWeight * product.quantity
    : product.quantity;
  const productAmount =
    parseNumberOrZero(product.customPricing) * adjustedQuantity;
  return enforce2Decimals(productAmount);
};

export function useUiUpdaters(updater: Updater<CreateEditCartUi>) {
  const updateBuyer = (buyer?: CartBuyerUi) =>
    updater((prev) => {
      prev.buyer = buyer;
      prev.buyerError = undefined;
    });

  const updateCartTitle = (cartTitle: string) =>
    updater((prev) => {
      prev.cartTitle = cartTitle;
    });

  const addProduct = (newProduct?: ProductUi) => {
    if (!newProduct) return;
    updater((prev) => {
      prev.products.push(structuredClone(newProduct)); // safe shallow copy to avoid errors in StrictMode
      addSellerTicketIfMissing(prev, newProduct);
      recalculateTotals(prev);
      recalculateButtonsState(prev);
    });
  };

  const calculateTotalProductLine = (productUi: ProductUi): string => {
    return `${formatDollarAmount(calcualteProductTotal(productUi))}`;
  };

  const updateQuantity = (productId: string, newValue: number) =>
    updater((prev) => {
      const product = prev.products.find((prod) => prod.id === productId);
      if (!product) return;
      product.quantity = newValue;
      product.quantityError = undefined;

      product.total = calculateTotalProductLine(product);
      recalculateTotals(prev);
      recalculateButtonsState(prev);
    });

  const updateCustomPrice = (productId: string, newValue: string) =>
    updater((prev) => {
      const product = prev.products.find((prod) => prod.id === productId);
      if (!product) return;
      const newPriceSnitized = Number(newValue);
      if (isNaN(newPriceSnitized)) return;
      product.customPricing = `${newValue}`;
      product.customPricingError = undefined;
      product.total = calculateTotalProductLine(product);
      recalculateTotals(prev);
      recalculateButtonsState(prev);
    });

  const deleteLine = (productId: string) =>
    updater((prev) => {
      const productToDeleteIndex = prev.products.findIndex(
        (p) => p.id === productId
      );
      if (productToDeleteIndex === NOT_FOUND) return;
      const productToDelete = prev.products[productToDeleteIndex];
      prev.products.splice(productToDeleteIndex, 1);

      const productsFromTheSameSeller = prev.products.filter(
        (product) => product.sellerId === productToDelete.sellerId
      );

      if (productsFromTheSameSeller.length === 0) {
        const sellerTicketToRemoveIndex = prev.sellers.findIndex(
          (sellerTicket) => sellerTicket.id === productToDelete.sellerId
        );
        if (sellerTicketToRemoveIndex !== NOT_FOUND) {
          prev.sellers.splice(sellerTicketToRemoveIndex, 1);
        }
      }

      recalculateTotals(prev);
      recalculateButtonsState(prev);
    });

  const updateDeliveryDate = (sellerId: string, newValue: Dayjs | null) =>
    updater((prev) => {
      const seller = prev.sellers.find((seller) => seller.id === sellerId);
      if (!seller) return;
      seller.deliveryDate = newValue;
      seller.deliveryDateError = undefined;
    });

  const onStartTimeChange = (sellerId: string, newValue: Dayjs | null) =>
    updater((prev) => {
      const seller = prev.sellers.find((seller) => seller.id === sellerId);
      if (!seller) return;
      seller.deliveryStartTime = newValue;
      validateTimes(seller);
    });

  const onEndTimeChange = (sellerId: string, newValue: Dayjs | null) =>
    updater((prev) => {
      const seller = prev.sellers.find((seller) => seller.id === sellerId);
      if (!seller) return;
      seller.deliveryEndTime = newValue;
      validateTimes(seller);
    });

  const validateTimes = (seller: WritableDraft<SellerLineUi>) => {
    if (!seller.deliveryStartTime || !seller.deliveryEndTime) return;
    const invalidEndDate =
      seller.deliveryStartTime.isAfter(seller.deliveryEndTime) ||
      seller.deliveryStartTime.isSame(seller.deliveryEndTime);
    seller.deliveryTimeError = invalidEndDate
      ? 'La hora final debe ser mayor a la hora de inicio.'
      : undefined;
  };

  const updateDeliveryNotes = (sellerId: string, newValue: string) =>
    updater((prev) => {
      const seller = prev.sellers.find((seller) => seller.id === sellerId);
      if (!seller) return;
      seller.deliveryNotes = newValue;
    });

  const addSellerTicketIfMissing = (
    draft: WritableDraft<CreateEditCartUi>,
    newProduct: ProductUi
  ) => {
    if (!newProduct.sellerId) return;
    const sellerAlreadyAdded = draft.sellers.some(
      (sellerTicket) => sellerTicket.id === newProduct.sellerId
    );
    if (sellerAlreadyAdded) return;
    const sellerTicket: SellerLineUi = {
      id: newProduct.sellerId,
      name: newProduct.sellerName ?? '',
      total: '',
      deliveryDate: null,
      deliveryStartTime: null,
      deliveryEndTime: null,
      deliveryNotes: '',
      minimumOrderAmount: `${formatDollarAmount(newProduct.sellerMinAmount)}`,
    };
    draft.sellers.push(sellerTicket);
  };

  const recalculateTotals = (input: WritableDraft<CreateEditCartUi>) => {
    const sellerTotalsBySellerId = new Map<string, number>();
    input.products.forEach((product) => {
      if (!product.sellerId) return;
      const acc = sellerTotalsBySellerId.get(product.sellerId) ?? 0;
      const productAmount = calcualteProductTotal(product);
      product.total = formatDollarAmount(productAmount);
      sellerTotalsBySellerId.set(product.sellerId, acc + productAmount);
    });

    // Calcualte Seller Tickets
    input.sellers.forEach((sellerTicket) => {
      const sellerTotal = sellerTotalsBySellerId.get(sellerTicket.id) ?? 0;
      sellerTicket.total = `${formatDollarAmount(sellerTotal)}`;
    });

    const total = input.sellers.reduce((acc, sellerLine) => {
      return acc + (formatedAmountToNumber(sellerLine.total) ?? 0);
    }, 0);
    input.total.total = `${formatDollarAmount(total)}`;
  };

  const recalculateButtonsState = (input: WritableDraft<CreateEditCartUi>) => {
    input.saveCartButton = {
      disabled:
        input.products.length === 0 ||
        input.total.total === formatDollarAmount(0),
    };
    input.finishCartButton = {
      disabled:
        input.products.length === 0 ||
        input.total.total === formatDollarAmount(0),
    };
  };

  return {
    updateBuyer,
    addProduct,
    updateQuantity,
    updateCustomPrice,
    deleteLine,
    updateDeliveryDate,
    onStartTimeChange,
    onEndTimeChange,
    updateDeliveryNotes,
    updateCartTitle,
  };
}
